import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--liturgical-primary)] hover:underline mb-8 font-['Nunito']">
          <ArrowLeft className="w-4 h-4" /> Back to Parish
        </Link>
        
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E0D5] p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-[var(--liturgical-primary)]" />
            <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26]">Terms of Service</h1>
          </div>
          
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-8">Last Updated: February 26, 2026</p>
          
          <div className="prose prose-slate max-w-none font-['Nunito'] text-[#2D2A26]">
            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">1. Acceptance of Terms</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Welcome to Parish. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use Parish.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                These Terms constitute a legally binding agreement between you and Parish. We may modify these Terms at any time, and such modifications will be effective immediately upon posting. Your continued use of Parish after any changes indicates your acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">2. Disclaimer of Affiliation with the Catholic Church</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Parish is an independent social platform created for Catholic communities. <strong>Parish is not owned, operated, endorsed, or affiliated with the Catholic Church, the Holy See, any diocese, archdiocese, parish, religious order, or any other Catholic institution.</strong>
              </p>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                The use of the name "Parish" and references to Catholic practices, liturgical seasons, and sacraments are for descriptive purposes only and do not imply any official connection to or endorsement by the Catholic Church or its institutions.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed mb-2">
                The Catholic Church and its affiliated entities bear no responsibility for:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2 mb-4">
                <li>The content posted by users on this platform</li>
                <li>The administration, moderation, or operation of this platform</li>
                <li>Any transactions conducted through the marketplace</li>
                <li>The accuracy of parish information, Mass times, or sacrament schedules listed</li>
                <li>Any disputes between users</li>
                <li>Data privacy or security practices of this platform</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed">
                Users seeking official information about Catholic parishes, sacraments, or Church teachings should contact their local parish or diocese directly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">3. Description of Service</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Parish is a social platform designed for Catholic communities, offering:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Parish community features including member directories, posts, and events</li>
                <li>A marketplace for buying and selling items and services</li>
                <li>A business directory for local Catholic-friendly businesses</li>
                <li>A Sacrament Finder to locate Mass, Reconciliation, and Adoration times</li>
                <li>Social networking features including messaging, friend connections, and groups</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">4. Eligibility</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                To use Parish, you must:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Be at least 13 years of age (or the minimum age in your jurisdiction)</li>
                <li>Be capable of forming a binding contract</li>
                <li>Not be prohibited from using the service under applicable law</li>
                <li>Provide accurate and complete registration information</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                If you are under 18, you represent that you have your parent or guardian's permission to use Parish.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">5. Account Responsibilities</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">You are responsible for:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Ensuring your account information is accurate and up-to-date</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                We reserve the right to suspend or terminate accounts that violate these Terms or our <Link to="/community-guidelines" className="text-[var(--liturgical-primary)] hover:underline">Community Guidelines</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">6. User Content</h2>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">6.1 Your Content</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                You retain ownership of content you post on Parish ("User Content"). By posting content, you grant Parish a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute your content in connection with operating and improving the platform.
              </p>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">6.2 Content Standards</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-2">You agree that your content will not:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Violate any applicable law or regulation</li>
                <li>Infringe on intellectual property rights of others</li>
                <li>Contain false, misleading, or deceptive information</li>
                <li>Be defamatory, harassing, threatening, or discriminatory</li>
                <li>Contain malware, spam, or unauthorized advertising</li>
                <li>Violate the privacy of others</li>
              </ul>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">6.3 Content Removal</h3>
              <p className="text-[#4A4A4A] leading-relaxed">
                We reserve the right to remove any content that violates these Terms or our Community Guidelines, without prior notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">7. Marketplace</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Parish provides a marketplace feature that allows users to list items and services. Please review our <Link to="/marketplace-terms" className="text-[var(--liturgical-primary)] hover:underline">Marketplace Terms</Link> for specific rules governing marketplace transactions.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                <strong>Important:</strong> Parish is a directory and listing service only. We do not process payments, handle shipping, verify listings, inspect items, or act as a party to any transaction between users. All transactions are conducted directly between buyers and sellers at their own risk.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed mb-2">
                <strong>Parish expressly disclaims all liability for:</strong>
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2 mb-4">
                <li>The quality, safety, legality, or availability of items or services listed</li>
                <li>The accuracy or truthfulness of listings, descriptions, or user representations</li>
                <li>The ability of sellers to sell items or buyers to pay for them</li>
                <li>Any disputes, claims, or damages arising from marketplace transactions</li>
                <li>Any loss, injury, or harm resulting from items purchased or services rendered</li>
                <li>Fraud, misrepresentation, or illegal activity by users</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed">
                Users engage in marketplace transactions entirely at their own risk and are solely responsible for evaluating the trustworthiness of other users and the suitability of items or services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">8. Parish Membership</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Users may request to join parishes on Parish. Parish administrators have the authority to:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Approve or deny membership requests</li>
                <li>Remove members from their parish</li>
                <li>Manage parish content and events</li>
                <li>Moderate parish-specific activities</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                Being a member of a parish on our platform does not constitute official membership in the actual Catholic parish.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">9. Prohibited Conduct</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-2">You agree not to:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Use Parish for any illegal purpose</li>
                <li>Impersonate any person or entity</li>
                <li>Interfere with or disrupt the platform or servers</li>
                <li>Attempt to gain unauthorized access to any part of the platform</li>
                <li>Use automated means to access the platform without permission</li>
                <li>Collect user information without consent</li>
                <li>Engage in harassment, bullying, or intimidation</li>
                <li>Post content that is anti-Catholic or disrespectful to religious beliefs</li>
                <li>Use the platform to promote non-Catholic religious services or beliefs in a manner contrary to the platform's purpose</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">10. Intellectual Property</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                The Parish platform, including its design, features, and content (excluding User Content), is owned by Parish and protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                You may not copy, modify, distribute, or create derivative works based on our platform without our express written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">11. Disclaimers</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                PARISH IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">11.1 No Responsibility for User Content</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Parish does not control, verify, endorse, or assume any responsibility for User Content, including but not limited to posts, comments, messages, marketplace listings, business directory entries, event information, or any other content submitted by users. Users are solely responsible for their own content and the consequences of posting it.
              </p>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">11.2 No Liability for User Interactions</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Parish is not responsible for any interactions between users, whether online or offline. This includes, without limitation, any disputes, injuries, damages, or losses arising from meetings, transactions, communications, or relationships formed through the platform.
              </p>

              <p className="text-[#4A4A4A] leading-relaxed mb-2">
                We do not endorse, guarantee, or assume responsibility for:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>User Content, conduct, or representations</li>
                <li>Marketplace transactions, listings, items, or services</li>
                <li>The accuracy of parish, sacrament, or event information</li>
                <li>The identity, background, or trustworthiness of users</li>
                <li>Third-party services or websites linked from our platform</li>
                <li>Any real-world interactions or meetings between users</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">12. Limitation of Liability</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PARISH SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">13. Indemnification</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                You agree to indemnify and hold harmless Parish, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the platform, your User Content, or your violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">14. Termination</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                We may suspend or terminate your account at any time for any reason, including violation of these Terms. You may also delete your account at any time through your account settings.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                Upon termination, your right to use Parish will immediately cease. Provisions that by their nature should survive termination will remain in effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">15. Governing Law</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Texas, United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">16. Dispute Resolution</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Any disputes arising from these Terms or your use of Parish shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                You agree to waive any right to participate in a class action lawsuit or class-wide arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">17. Contact Us</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                If you have questions about these Terms, please contact us at:
              </p>
              <div className="bg-[#F9F7F2] rounded-lg p-4 mt-4">
                <p className="text-[#4A4A4A]"><strong>Email:</strong> <a href="mailto:legal@parish.com" className="text-[var(--liturgical-primary)] hover:underline">legal@parish.com</a></p>
              </div>
            </section>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            See also: <Link to="/privacy" className="text-[var(--liturgical-primary)] hover:underline">Privacy Policy</Link> · <Link to="/cookies" className="text-[var(--liturgical-primary)] hover:underline">Cookie Policy</Link> · <Link to="/community-guidelines" className="text-[var(--liturgical-primary)] hover:underline">Community Guidelines</Link> · <Link to="/marketplace-terms" className="text-[var(--liturgical-primary)] hover:underline">Marketplace Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
